/**
 * German License Plate City Codes
 *
 * Validation data is loaded from the Zulassungsstellen CSV at runtime
 * and cached in memory. This ensures the app always matches the current
 * list of supported Zulassungsstellen.
 *
 * Usage:
 *   - Call `preloadCityCodes()` early (e.g. on app mount) to warm the cache
 *   - `isValidCityCode()` returns true before the cache is ready (permissive)
 *     to avoid false rejections, then becomes strict once loaded
 */

import { loadZulassungsstellen } from '@/services/zulassungsstellenService'

let validCodesCache: Set<string> | null = null
let loadPromise: Promise<Set<string>> | null = null

async function loadValidCodes(): Promise<Set<string>> {
  if (validCodesCache) return validCodesCache
  if (loadPromise) return loadPromise

  loadPromise = (async () => {
    const stellen = await loadZulassungsstellen()
    validCodesCache = new Set(stellen.map(s => s.regionCode.toUpperCase()))
    return validCodesCache
  })()

  return loadPromise
}

/**
 * Preload the city codes cache. Call this once at app startup.
 */
export function preloadCityCodes(): void {
  void loadValidCodes()
}

/**
 * Check if a city code exists in the database.
 * Returns true if cache is not loaded yet (permissive) to avoid false rejections.
 */
export function isValidCityCode(cityCode: string): boolean {
  if (!cityCode) return false
  if (!validCodesCache) return true // permissive until loaded
  return validCodesCache.has(cityCode.toUpperCase())
}

/**
 * Async variant — waits for cache before answering.
 */
export async function isValidCityCodeAsync(cityCode: string): Promise<boolean> {
  if (!cityCode) return false
  const codes = await loadValidCodes()
  return codes.has(cityCode.toUpperCase())
}

/**
 * Filter city codes by search term (uses cached data; empty if not loaded yet).
 */
export function filterCityCodes(searchTerm: string): string[] {
  if (!validCodesCache) return []
  const upper = searchTerm.toUpperCase()
  const all = Array.from(validCodesCache)
  const matching = upper ? all.filter(c => c.startsWith(upper)) : all

  matching.sort((a, b) => {
    if (a === upper) return -1
    if (b === upper) return 1
    if (a.length !== b.length) return a.length - b.length
    return a.localeCompare(b)
  })

  const limit = upper.length >= 2 ? 15 : 10
  return matching.slice(0, limit)
}
